import React, { useEffect, useMemo, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { getLangFromCookie, LANG_COOKIE } from "../../../../Utils/LangCookie";
import CategoryCard from "./CategoryCard";
import { ProductsAPI } from "../../../../api/Product";

const getCurrentLang = () =>
  getLangFromCookie() || localStorage.getItem(LANG_COOKIE) || "ZH";

const safeDecode = (s) => {
  try {
    return decodeURI(String(s || ""));
  } catch {
    return String(s || "");
  }
};

const GROUP_IMAGE_MAP = {
  洗手和手部消毒: "/img/products/B2B/hand-wash.webp",
  食品衛生化學品: "/img/products/B2B/chemicals.webp",
  器械清潔: "/img/products/B2B/equipment-cleaning.webp",
  一次性衛生耗材: "/img/products/B2B/disposable.webp",
  設備: "/img/products/B2B/equipment.webp",
  工具: "/img/products/B2B/tools.webp",
};

const fallbackGroupImage = "/img/empty.webp";

const SECTION_TITLE = {
  ZH: "商品分類",
  EN: "Product Category",
  JP: "商品カテゴリー",
};

const normalizeLang = (code) => (code === "EN" || code === "JP" ? code : "ZH");

function getGroupLabel(group) {
  return (
    group?.group_name ||
    group?.groupName ||
    group?.label ||
    group?.group_id ||
    ""
  );
}

function normalizeGroup(group) {
  const groupID = String(group?.group_id || group?.groupID || "").trim();
  const label = getGroupLabel(group);

  return {
    ...group,
    group_id: groupID,
    label,
    to: `/商品資訊/group/${encodeURIComponent(groupID)}`,
    img: GROUP_IMAGE_MAP[label] || fallbackGroupImage,
  };
}

export default function CategoryGroup({
  currentHref,
  activeGroupID = "",
  onSelectCategory,
  onSelectProductGroup,
  selectedLayout = false,
}) {
  const [lang, setLang] = useState(normalizeLang(getCurrentLang()));
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    const onChange = () => setLang(normalizeLang(getCurrentLang()));
    window.addEventListener("app:languageChanged", onChange);
    return () => window.removeEventListener("app:languageChanged", onChange);
  }, []);

  useEffect(() => {
    let ignore = false;

    async function loadGroups() {
      try {
        const data = await ProductsAPI.productGroups({ lang });

        if (!ignore) {
          setGroups((Array.isArray(data) ? data : []).map(normalizeGroup));
        }
      } catch (err) {
        console.error("Failed to load product groups:", err);
        if (!ignore) setGroups([]);
      }
    }

    loadGroups();

    return () => {
      ignore = true;
    };
  }, [lang]);

  const GROUP_DISPLAY_ORDER = [
    "洗手和手部消毒",
    "食品衛生化學品",
    "一次性衛生耗材",
    "設備",
    "器械清潔",
    "工具",
  ];

  const items = useMemo(() => {
    const orderMap = new Map(
      GROUP_DISPLAY_ORDER.map((label, index) => [label, index]),
    );

    return groups
      .filter((g) => g.group_id)
      .sort((a, b) => {
        const aIndex = orderMap.has(a.label) ? orderMap.get(a.label) : 999;
        const bIndex = orderMap.has(b.label) ? orderMap.get(b.label) : 999;

        if (aIndex !== bIndex) return aIndex - bIndex;

        return String(a.label || "").localeCompare(
          String(b.label || ""),
          "zh-Hant",
        );
      });
  }, [groups]);

  const isActiveHref = (target) => {
    if (!currentHref) return false;
    const a = safeDecode(currentHref);
    const b = safeDecode(target);
    return a === b || a.startsWith(b + "/");
  };

  const handleSelectGroup = (group) => {
    onSelectProductGroup?.(group);

    if (!onSelectProductGroup && group?.to) {
      onSelectCategory?.(group.to);
    }

    window.dispatchEvent(
      new CustomEvent("app:productGroupSelected", {
        detail: { group },
      }),
    );
  };

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        zIndex: 0,
        bgcolor: "transparent",
        pt: selectedLayout ? { xs: 5, md: 8 } : 0,
        pb: selectedLayout ? { xs: 5, md: 8 } : 0,
        mb: selectedLayout ? 0 : { xs: 4, md: 5 },
        "&::before": selectedLayout
          ? {
              content: '""',
              position: "absolute",
              top: 0,
              bottom: 0,
              left: "50%",
              width: "100vw",
              transform: "translateX(-50%)",
              bgcolor: "#fff",
              zIndex: -1,
            }
          : undefined,
      }}
    >
      <Divider sx={{ color: "black", mb: 2 }}>
        <Typography
          align="center"
          sx={{
            fontWeight: 700,
            fontSize: { xs: 18, sm: 24, md: 28, lg: 25 },
            
          }}
        >
          {SECTION_TITLE[lang] || SECTION_TITLE.ZH}
        </Typography>
      </Divider>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, minmax(0, 1fr))",
          },
          gap: { xs: 2, md: 2.5 },
        }}
      >
        {items.map((item) => (
          <CategoryCard
            key={item.group_id}
            label={item.label}
            img={item.img}
            active={
              String(activeGroupID || "") === String(item.group_id) ||
              isActiveHref(item.to)
            }
            onClick={() => handleSelectGroup(item)}
            showArrow
            objfit="contain"
            imgBg="#fff"
            imgPadding={2}
            labelWidth="100%"
            labelPlacement="top"
            aspectRatio="16 / 7"
            overlayLabel
            overlayLabelAlign="left"
            overlayLabelPlayIcon
            overlayLabelFontSize={{ xs: 18, sm: 40, md: 25, lg: 25 }}
          />
        ))}
      </Box>
    </Box>
  );
}
